import { useRef, useState } from "react";

// One message in the transcript.
//
// USER TEXT IS NEVER RENDERED AS HTML. Agent and system copy is, because the model
// is allowed a little <b> emphasis — but only after the server has escaped
// everything and re-allowed that one tag (sanitizeRich in util/sanitize.ts). The
// asymmetry is the point: what the user typed comes back to them verbatim as text,
// and what the model wrote passes a whitelist first.

export type BubbleRole = "user" | "agent" | "system";

export function Bubble({ role, html }: { role: BubbleRole; html: string }) {
  if (role === "user") {
    return (
      <div className="row user">
        <div className="bubble user">{html}</div>
      </div>
    );
  }
  return (
    <div className="row">
      <div className="bubble-col">
        <div className={`bubble ${role}`} dangerouslySetInnerHTML={{ __html: html }} />
        {/* Only agent answers, not system status/error text — there is nothing in a
            "your session expired" line worth putting on a clipboard. */}
        {role === "agent" && <CopyAnswerButton html={html} />}
      </div>
    </div>
  );
}

/** Copies the answer as plain text. The bubble's only markup is the server's own
 *  whitelisted <b>, so reading it back through an element's textContent is exactly
 *  the same trust boundary dangerouslySetInnerHTML already crosses, not a new one. */
function CopyAnswerButton({ html }: { html: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function handleCopy() {
    const holder = document.createElement("div");
    holder.innerHTML = html;
    const text = holder.textContent ?? "";
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard access can fail outside a secure context; the text is still
         on screen and selectable by hand. */
    }
  }

  return (
    <button
      type="button"
      className="bubble-copy"
      onClick={() => void handleCopy()}
      aria-label={copied ? "Copied" : "Copy this answer"}
      title={copied ? "Copied" : "Copy"}
    >
      {copied ? "✓" : "⧉"}
    </button>
  );
}
